import { createAsyncThunk } from "@reduxjs/toolkit"
import { CoreState } from "../state/core.state"
import { coreActions } from "../slices/core.slice"
import { navigateTo401 } from "../utils/dom"
import { initAIPanel } from "../utils/ai-panel"
import { authorizedGet, getApplicationsUrl } from "../utils/api"
import { trackApplicationsLoaded } from "../utils/analytics"
import {
  getAppUrlDomain,
  processApplications,
  extractFeatureFlags
} from "../utils/app-config"
import { GLOBAL_TENANT_HINT } from "../constants/tenant"
import * as singleSpa from "single-spa"

export const getAppConfig = createAsyncThunk<
  void,
  any,
  { state: CoreState }
>(
  "core/getAppConfig",
  async (_arg, { getState, dispatch }) => {
    const storeData = getState()
    try {
      // Inside the try: a missing ext.tenantId or absent global hint used
      // to throw BEFORE the try, producing an unhandled thunk rejection
      // that left the global loader spinning forever with no navigation.
      const globalTenant = storeData?.userInfo?.ext?.tenantId?.find(
        (a) => a?.hint === GLOBAL_TENANT_HINT
      )
      if (!globalTenant?.id) {
        throw new Error("No global tenant hint found in userInfo")
      }

      const appJSONUrl = getApplicationsUrl(globalTenant.id)
      const userToken = storeData.userToken!
      const res = await authorizedGet(appJSONUrl, userToken)

      const { appTenantVal, domain } = getAppUrlDomain()
      const flags = extractFeatureFlags(res)
      let isSSEEUURL = false

      const ADMIN_SCOPES = ["*", "admin:*", "admin:read"]
      const userScopes = storeData?.userInfo?.scope?.split(" ")
      var isAdmin = false
      for (const scope of ADMIN_SCOPES){
        if(userScopes.includes(scope))
        {
          isAdmin = true
          break
        }
      }

      // Don't show this if current user is not an admin
      if (flags.showHomepage && isAdmin) {
        dispatch(coreActions.setShowHomepage(true))
        singleSpa.registerApplication({
          name: "@forcepoint/homepageui",
          app: () => {
            // BUG #7: purge SystemJS's cached failure and rethrow so
            // single-spa marks LOAD_ERROR and retries on the next routing
            // event. An empty-lifecycle fallback would be cached as
            // "loaded" and leave /home permanently blank for the session.
            return (System.import("@forcepoint/homepageui") as Promise<
              singleSpa.LifeCycles<any>
            >).catch((error) => {
              console.error(
                "[getAppConfig] Failed to load @forcepoint/homepageui:",
                error
              )
              try {
                const sys = System as any
                sys.delete?.(sys.resolve("@forcepoint/homepageui"))
              } catch {
                // nothing to purge
              }
              throw error
            })
          },
          activeWhen: "/home"
        })
      }
      if (flags.useNewIframe) {
        dispatch(coreActions.setUseNewIframe(true))
      }
      if (flags.isSSEEUURL) {
        isSSEEUURL = true
        dispatch(coreActions.setUseSSEEUURL(true))
      }
      if (flags.isAIEnabled) {
        initAIPanel({ getState, dispatch })
      }

      const processedApps = processApplications(
        res,
        appTenantVal,
        domain,
        storeData,
        isSSEEUURL
      )

      dispatch(coreActions.setApplications(processedApps))

      trackApplicationsLoaded(processedApps)
    } catch {
      navigateTo401({ dispatch })
    }
  }
)
